import type { AppletAgentMapping } from './types'

function compareMappings(a: AppletAgentMapping, b: AppletAgentMapping): number {
  return (
    Number(b.is_primary_producer) - Number(a.is_primary_producer) ||
    Number(Boolean(b.langfuse_prompt_key)) - Number(Boolean(a.langfuse_prompt_key)) ||
    a.page_id.localeCompare(b.page_id) ||
    a.data_element_key.localeCompare(b.data_element_key) ||
    a.agent_identifier.localeCompare(b.agent_identifier)
  )
}

export function getPreferredMapping(
  mappings: AppletAgentMapping[],
): AppletAgentMapping | undefined {
  return [...mappings].sort(compareMappings)[0]
}

export function getMappingsForElement(
  mappings: AppletAgentMapping[],
  dataElementKey?: string,
): AppletAgentMapping[] {
  if (!dataElementKey) return []
  return mappings.filter((mapping) => mapping.data_element_key === dataElementKey)
}

export function getMappingsForAgent(
  mappings: AppletAgentMapping[],
  agentIdentifier?: string,
): AppletAgentMapping[] {
  if (!agentIdentifier) return []
  return mappings.filter((mapping) => mapping.agent_identifier === agentIdentifier)
}

export function getUniqueDataElements(
  mappings: AppletAgentMapping[],
): AppletAgentMapping[] {
  const grouped = new Map<string, AppletAgentMapping[]>()

  for (const mapping of mappings) {
    const current = grouped.get(mapping.data_element_key) ?? []
    current.push(mapping)
    grouped.set(mapping.data_element_key, current)
  }

  return Array.from(grouped.values())
    .map((group) => getPreferredMapping(group))
    .filter((mapping): mapping is AppletAgentMapping => Boolean(mapping))
}

export function resolveSelectedMapping(
  mappings: AppletAgentMapping[],
  options: {
    dataElementKey?: string
    agentIdentifier?: string
  },
): AppletAgentMapping | undefined {
  const { dataElementKey, agentIdentifier } = options

  if (dataElementKey && agentIdentifier) {
    const exactMatches = mappings.filter(
      (mapping) =>
        mapping.data_element_key === dataElementKey &&
        mapping.agent_identifier === agentIdentifier,
    )
    const exact = getPreferredMapping(exactMatches)
    if (exact) return exact
  }

  const byElement = getPreferredMapping(getMappingsForElement(mappings, dataElementKey))
  if (byElement) return byElement

  const byAgent = getPreferredMapping(getMappingsForAgent(mappings, agentIdentifier))
  if (byAgent) return byAgent

  return getPreferredMapping(mappings)
}
